(() => {
  const ELEMENT_NAME = 'xo-cart-invoice';
  const REQUEST_VALUE = 'Ναι';
  const SAVE_DELAY = 600;
  let checkoutListenerReady = false;
  let bypassCheckoutSubmit = false;

  const getRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getComponents = () => Array.from(document.querySelectorAll(ELEMENT_NAME));

  const getInvoiceInputs = () =>
    Array.from(document.querySelectorAll(`${ELEMENT_NAME} [data-cart-invoice-attribute]`));

  const getEditableInputs = () =>
    Array.from(document.querySelectorAll(`${ELEMENT_NAME} [data-cart-invoice-field]`));

  const hasInvoiceValue = () =>
    getEditableInputs().some((input) => input.value.trim() !== '');

  const syncRequestFields = () => {
    const value = hasInvoiceValue() ? REQUEST_VALUE : '';
    document
      .querySelectorAll(`${ELEMENT_NAME} [data-cart-invoice-request]`)
      .forEach((input) => {
        input.value = value;
      });
    return value;
  };

  const collectAttributes = () => {
    syncRequestFields();
    return getInvoiceInputs().reduce((attributes, input) => {
      const attribute = input.dataset.cartInvoiceAttribute;
      if (attribute) {
        attributes[attribute] = input.value.trim();
      }
      return attributes;
    }, {});
  };

  const postAttributes = async () => {
    const response = await fetch(`${getRoot()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes: collectAttributes() }),
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return response.json();
  };

  const nativeSubmit = (form, submitter) => {
    if (submitter?.name && !form.querySelector(`input[type='hidden'][name='${submitter.name}']`)) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || submitter.textContent?.trim() || 'checkout';
      form.appendChild(hidden);
    }
    HTMLFormElement.prototype.submit.call(form);
  };

  const saveAll = async () => {
    const components = getComponents();
    if (!components.length) return;

    components.forEach((component) => component.setSaving());
    try {
      await postAttributes();
      components.forEach((component) => component.setSaved());
    } catch (error) {
      components.forEach((component) => component.setError());
      throw error;
    }
  };

  const ensureCheckoutListener = () => {
    if (checkoutListenerReady) return;
    checkoutListenerReady = true;

    document.addEventListener(
      'submit',
      async (event) => {
        if (bypassCheckoutSubmit) return;

        const form = event.target;
        const submitter = event.submitter;
        const action = form.getAttribute('action') || '';
        const isCheckoutSubmit = action.includes('/checkout') || submitter?.name === 'checkout';

        if (!isCheckoutSubmit || !getComponents().some((component) => component.isDirty)) {
          return;
        }

        event.preventDefault();
        await saveAll();
        bypassCheckoutSubmit = true;
        nativeSubmit(form, submitter);
        bypassCheckoutSubmit = false;
      },
      true
    );
  };

  class CartInvoice extends HTMLElement {
    constructor() {
      super();
      this.inputs = [];
      this.saveButton = null;
      this.status = null;
      this.cancelTrigger = null;
      this.saveTimer = null;
      this.isDirty = false;
      this.handleInput = this.handleInput.bind(this);
      this.handleSaveClick = this.handleSaveClick.bind(this);
    }

    connectedCallback() {
      this.inputs = Array.from(this.querySelectorAll('[data-cart-invoice-attribute]'));
      this.saveButton = this.querySelector('[data-cart-invoice-save]');
      this.status = this.querySelector('[data-cart-invoice-status]');
      this.cancelTrigger = this.querySelector('[data-cart-invoice-cancel]');
      this.inputs.forEach((input) => {
        input.addEventListener('input', this.handleInput);
        input.addEventListener('change', this.handleInput);
      });
      this.saveButton?.addEventListener('click', this.handleSaveClick);
      syncRequestFields();
      ensureCheckoutListener();
    }

    disconnectedCallback() {
      window.clearTimeout(this.saveTimer);
      this.inputs.forEach((input) => {
        input.removeEventListener('input', this.handleInput);
        input.removeEventListener('change', this.handleInput);
      });
      this.saveButton?.removeEventListener('click', this.handleSaveClick);
    }

    handleInput(event) {
      const input = event.target;
      const field = input.dataset.cartInvoiceField;
      this.syncMatchingInputs(field, input.value, input);
      syncRequestFields();
      this.isDirty = true;
      this.setIdle();
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.save(), SAVE_DELAY);
    }

    handleSaveClick(event) {
      event.preventDefault();
      this.save(true);
    }

    syncMatchingInputs(field, value, source) {
      if (!field) return;
      document
        .querySelectorAll(`${ELEMENT_NAME} [data-cart-invoice-field='${field}']`)
        .forEach((input) => {
          if (input !== source && input.value !== value) {
            input.value = value;
          }
        });
    }

    async save(closeAfterSave = false) {
      window.clearTimeout(this.saveTimer);
      this.setSaving();
      try {
        await postAttributes();
        getComponents().forEach((component) => component.setSaved());
        if (closeAfterSave) {
          this.cancelTrigger?.click();
        }
      } catch (error) {
        getComponents().forEach((component) => component.setError());
      }
    }

    setIdle() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.removeAttribute('xo-status');
    }

    setSaving() {
      if (!this.status) return;
      this.status.textContent = 'Αποθήκευση...';
      this.status.removeAttribute('xo-status');
    }

    setSaved() {
      this.isDirty = false;
      if (!this.status) return;
      this.status.textContent = 'Αποθηκεύτηκε';
      this.status.setAttribute('xo-status', 'success');
    }

    setError() {
      if (!this.status) return;
      this.status.textContent = 'Δεν αποθηκεύτηκε';
      this.status.setAttribute('xo-status', 'error');
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, CartInvoice);
  }
})();
